import * as XLSX from "xlsx";

export type TenderColumnMapping = {
  code: number | null;
  description: number | null;
  unit: number | null;
  quantity: number | null;
};

export type ParsedTenderSheet = {
  name: string;
  rows: unknown[][];
  detectedHeaderRow: number;
  detectedMapping: TenderColumnMapping;
  detectionScore: number;
};

export type ParsedTenderWorkbook = {
  fileName: string;
  sheets: ParsedTenderSheet[];
  suggestedSheetIndex: number;
};

export type TenderSourceLine = {
  row_number: number;
  original_code: string | null;
  original_description: string;
  original_unit: string | null;
  quantity: number | null;
  raw_data: {
    cells: unknown[];
  };
};

function normalize(value: unknown) {
  const text = String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

  return text;
}

function looksLikeDescription(label: string) {
  return (
    label === "descricao" ||
    label === "designacao" ||
    label === "trabalhos" ||
    label.includes("descricao dos trabalhos") ||
    label.includes("designacao dos trabalhos") ||
    label.includes("descricao trabalho") ||
    label.includes("trabalhos a executar") ||
    label.includes("trabalhos a realizar")
  );
}

function looksLikeCode(label: string) {
  return (
    label === "art" ||
    label === "art." ||
    label === "artº" ||
    label === "artigo" ||
    label === "codigo" ||
    label === "item" ||
    label === "ref" ||
    label === "referencia" ||
    label.includes("codigo artigo")
  );
}

function looksLikeUnit(label: string) {
  if (label.includes("preco") || label.includes("unitario")) return false;

  return (
    label === "un" ||
    label === "un." ||
    label === "unid" ||
    label === "unid." ||
    label === "unidade" ||
    label === "ud" ||
    label === "u.m." ||
    label === "um"
  );
}

function looksLikeQuantity(label: string) {
  return (
    label === "qtd" ||
    label === "qtd." ||
    label === "qt" ||
    label === "quant" ||
    label === "quant." ||
    label === "quantidade" ||
    label === "quantidades"
  );
}

function detectHeader(rows: unknown[][]) {
  let bestScore = -1;
  let bestHeaderRow = 0;
  let bestMapping: TenderColumnMapping = {
    code: null,
    description: null,
    unit: null,
    quantity: null,
  };

  const maxRows = Math.min(rows.length, 60);

  for (let rowIndex = 0; rowIndex < maxRows; rowIndex += 1) {
    const row = rows[rowIndex] ?? [];
    const mapping: TenderColumnMapping = {
      code: null,
      description: null,
      unit: null,
      quantity: null,
    };

    row.forEach((cell, columnIndex) => {
      const label = normalize(cell);
      if (!label) return;

      if (mapping.description === null && looksLikeDescription(label)) {
        mapping.description = columnIndex;
      }
      if (mapping.quantity === null && looksLikeQuantity(label)) {
        mapping.quantity = columnIndex;
      }
      if (mapping.unit === null && looksLikeUnit(label)) {
        mapping.unit = columnIndex;
      }
      if (mapping.code === null && looksLikeCode(label)) {
        mapping.code = columnIndex;
      }
    });

    let score = 0;
    if (mapping.description !== null) score += 6;
    if (mapping.quantity !== null) score += 4;
    if (mapping.unit !== null) score += 3;
    if (mapping.code !== null) score += 1;

    const nonEmpty = row.filter((value) => String(value ?? "").trim() !== "").length;
    if (nonEmpty >= 3) score += 1;

    if (score > bestScore) {
      bestScore = score;
      bestHeaderRow = rowIndex;
      bestMapping = mapping;
    }
  }

  return {
    headerRow: bestHeaderRow,
    mapping: bestMapping,
    score: bestScore,
  };
}

function worksheetRows(sheet: XLSX.WorkSheet) {
  if (!sheet["!ref"]) return [] as unknown[][];

  const range = XLSX.utils.decode_range(sheet["!ref"]);
  const rows: unknown[][] = [];

  // Pad from row/column zero so array indexes stay identical to real Excel
  // coordinates. This matters later when we export prices back to the exact
  // owner-provided row without shifting anything.
  for (let rowIndex = 0; rowIndex <= range.e.r; rowIndex += 1) {
    const row: unknown[] = [];

    for (let columnIndex = 0; columnIndex <= range.e.c; columnIndex += 1) {
      const address = XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex });
      const cell = sheet[address];
      row.push(cell?.v ?? "");
    }

    rows.push(row);
  }

  return rows;
}

export async function parseTenderWorkbook(file: File): Promise<ParsedTenderWorkbook> {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, {
    type: "array",
    cellFormula: true,
    cellDates: false,
    dense: false,
  });

  const sheets: ParsedTenderSheet[] = workbook.SheetNames.map((name) => {
    const worksheet = workbook.Sheets[name];
    const rows = worksheetRows(worksheet);
    const detected = detectHeader(rows);

    return {
      name,
      rows,
      detectedHeaderRow: detected.headerRow,
      detectedMapping: detected.mapping,
      detectionScore: detected.score,
    };
  });

  let suggestedSheetIndex = 0;
  sheets.forEach((sheet, index) => {
    if (
      sheet.detectionScore >
      (sheets[suggestedSheetIndex]?.detectionScore ?? Number.NEGATIVE_INFINITY)
    ) {
      suggestedSheetIndex = index;
    }
  });

  return {
    fileName: file.name,
    sheets,
    suggestedSheetIndex,
  };
}

export function columnLabel(columnIndex: number) {
  let value = columnIndex + 1;
  let label = "";

  while (value > 0) {
    const remainder = (value - 1) % 26;
    label = String.fromCharCode(65 + remainder) + label;
    value = Math.floor((value - 1) / 26);
  }

  return label;
}

export function suggestMappingForHeaderRow(
  sheet: ParsedTenderSheet,
  headerRow: number,
): TenderColumnMapping {
  const row = sheet.rows[headerRow] ?? [];
  const mapping: TenderColumnMapping = {
    code: null,
    description: null,
    unit: null,
    quantity: null,
  };

  row.forEach((cell, columnIndex) => {
    const label = normalize(cell);
    if (!label) return;

    if (mapping.description === null && looksLikeDescription(label)) {
      mapping.description = columnIndex;
    }
    if (mapping.quantity === null && looksLikeQuantity(label)) {
      mapping.quantity = columnIndex;
    }
    if (mapping.unit === null && looksLikeUnit(label)) {
      mapping.unit = columnIndex;
    }
    if (mapping.code === null && looksLikeCode(label)) {
      mapping.code = columnIndex;
    }
  });

  return mapping;
}

export function headersForRow(sheet: ParsedTenderSheet, headerRow: number) {
  const row = sheet.rows[headerRow] ?? [];
  return row.map((value, index) => ({
    index,
    label: String(value ?? "").trim() || columnLabel(index),
  }));
}

function parseNumber(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  const source = String(value ?? "").trim();
  if (!source) return null;

  const cleaned = source.replace(/\s/g, "");

  let normalized = cleaned;

  if (cleaned.includes(",") && cleaned.includes(".")) {
    if (cleaned.lastIndexOf(",") > cleaned.lastIndexOf(".")) {
      normalized = cleaned.replace(/\./g, "").replace(",", ".");
    } else {
      normalized = cleaned.replace(/,/g, "");
    }
  } else if (cleaned.includes(",")) {
    normalized = cleaned.replace(",", ".");
  }

  const number = Number(normalized);
  return Number.isFinite(number) ? number : null;
}

function stringValue(value: unknown) {
  return String(value ?? "").trim();
}

export function extractTenderSourceLines(
  sheet: ParsedTenderSheet,
  headerRow: number,
  mapping: TenderColumnMapping,
): TenderSourceLine[] {
  if (mapping.description === null) return [];

  const lines: TenderSourceLine[] = [];

  for (let rowIndex = headerRow + 1; rowIndex < sheet.rows.length; rowIndex += 1) {
    const row = sheet.rows[rowIndex] ?? [];
    const description = stringValue(row[mapping.description]);

    if (!description) continue;

    const normalizedDescription = normalize(description);
    if (
      normalizedDescription === "total" ||
      normalizedDescription === "subtotal" ||
      normalizedDescription.startsWith("total ")
    ) {
      continue;
    }

    const unit =
      mapping.unit === null ? "" : stringValue(row[mapping.unit]);
    const quantity =
      mapping.quantity === null ? null : parseNumber(row[mapping.quantity]);
    const code =
      mapping.code === null ? "" : stringValue(row[mapping.code]);

    // Chapter/title rows normally have no unit and no quantity. Preserve actual
    // priceable rows, including legitimate zero-quantity rows.
    if (!unit && quantity === null) continue;

    lines.push({
      row_number: rowIndex + 1,
      original_code: code || null,
      original_description: description,
      original_unit: unit || null,
      quantity,
      raw_data: {
        cells: row.map((value) => {
          if (
            value === null ||
            typeof value === "string" ||
            typeof value === "number" ||
            typeof value === "boolean"
          ) {
            return value;
          }

          return String(value);
        }),
      },
    });
  }

  return lines;
}

export async function sha256File(file: File) {
  const buffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", buffer);

  return Array.from(new Uint8Array(digest))
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase();
}
